import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import RotateRightRounded from "@mui/icons-material/RotateRightRounded";
import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { format } from "date-fns";

import { DocumentResponse } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { formatContentType, formatFileSize } from "@/common/helper/fileDownloadHelper";
import { useTranslation } from "@/translations";

import { Rotation } from "./MergeFilesDialog.types";
import { useMergeRowPreview } from "./hooks/useMergeRowPreview";

interface Props {
  file: DocumentResponse;
  fetchImageBlob: (file: DocumentResponse) => Promise<Blob>;
  disabled?: boolean;
  rotationDegrees: Rotation;
  onRotate: () => void;
}

export function MergeFileRow({ file, fetchImageBlob, disabled, rotationDegrees, onRotate }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: file.documentId });
  const isImage = file.contentType.startsWith("image/");
  const { blobUrl, isLoading, isUnavailable } = useMergeRowPreview(file, fetchImageBlob, !isImage);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const previewRotationSx = {
    transform: `rotate(${rotationDegrees}deg)`,
    transition: "transform 150ms ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      gap={2}
      sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        aria-label={t("dropzone.merge.dragHandleAria")}
        sx={{ cursor: "grab", touchAction: "none" }}
        disabled={disabled}
      >
        <DragIndicatorRounded />
      </IconButton>

      <Box
        sx={{
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={previewRotationSx}>
          {!isImage ? (
            <DescriptionRounded color="action" fontSize="large" />
          ) : isLoading ? (
            <CircularProgress size={24} aria-label={t("dropzone.merge.previewLoading")} />
          ) : blobUrl && !isUnavailable ? (
            <Box
              component="img"
              src={blobUrl}
              alt=""
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <DescriptionRounded color="action" fontSize="large" />
          )}
        </Box>
      </Box>

      <IconButton size="small" onClick={onRotate} aria-label={t("dropzone.merge.rotateAria")} disabled={disabled}>
        <RotateRightRounded />
      </IconButton>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap title={file.fileName}>
          {file.fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {formatContentType(file.contentType)} · {formatFileSize(file.fileSizeBytes)} ·{" "}
          {format(file.assignedAtUtc, "Pp", { locale: locales[language] })}
        </Typography>
      </Stack>
    </Stack>
  );
}
